import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CancelIcon from "@mui/icons-material/Cancel";
import { format } from "date-fns";
import Header from "../../components/Header";
import DataGridCustomToolbar from "../../components/DataGridCustomToolbar";
import { useGetRatingListQuery } from "../../state/api";

const experienceColor = {
  good: "#2e7d32",
  moderate: "#ed6c02",
  bad: "#d32f2f",
};

const RatingAndFeedback = () => {
  const theme = useTheme();
  const { data, isLoading } = useGetRatingListQuery();
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  const rows = (data && data.data) || [];

  const columns = [
    { field: "giverName", headerName: "Given By", flex: 0.6, minWidth: 130 },
    { field: "userName", headerName: "Given To", flex: 0.6, minWidth: 130 },
    {
      field: "ratingPoints",
      headerName: "Rating",
      flex: 0.6,
      minWidth: 130,
      renderCell: (params) => (
        <Rating value={params.value} readOnly size="small" />
      ),
    },
    {
      field: "experience",
      headerName: "Experience",
      flex: 0.4,
      minWidth: 100,
      renderCell: (params) => (
        <span
          style={{
            backgroundColor: experienceColor[params.value] || "#555",
            color: "white",
            padding: "4px 10px",
            borderRadius: "4px",
            textTransform: "capitalize",
            fontSize: "0.8rem",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "whatYouFeel",
      headerName: "Feedback",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => params.value || "N/A",
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 0.5,
      minWidth: 110,
      renderCell: (params) => format(new Date(params.value), "yyyy-MM-dd"),
    },
    {
      field: "actions",
      headerName: "Details",
      flex: 0.4,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton color="info" onClick={() => setSelectedRow(params.row)}>
          <Typography variant="body2" sx={{ textDecoration: "underline" }}>
            View
          </Typography>
        </IconButton>
      ),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Header title="REVIEWS & RATINGS" />
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.success,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.secondary[200]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading || !data}
          getRowId={(row) => row._id}
          rows={rows}
          columns={columns}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[20, 50, 100]}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch, columns, rowData: rows },
          }}
        />
      </Box>

      <Dialog
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" pt={1} pr={1}>
          <IconButton color="error" onClick={() => setSelectedRow(null)}>
            <CancelIcon />
          </IconButton>
        </Box>
        {selectedRow && (
          <>
            <DialogTitle sx={{ pt: 0 }}>
              {selectedRow.giverName} → {selectedRow.userName}
            </DialogTitle>
            <DialogContent>
              <Rating value={selectedRow.ratingPoints} readOnly sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Looks:</b> {selectedRow.userLooks} &nbsp; <b>Stylish:</b>{" "}
                {selectedRow.stylish} &nbsp; <b>Speech:</b>{" "}
                {selectedRow.speech} &nbsp; <b>Helpful:</b>{" "}
                {selectedRow.helpful} &nbsp; <b>Attractive:</b>{" "}
                {selectedRow.attractive}
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                <b>What they felt:</b> {selectedRow.whatYouFeel || "N/A"}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <b>Good habits:</b> {selectedRow.goodHabits || "N/A"}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <b>Bad habits:</b> {selectedRow.badHabits || "N/A"}
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RatingAndFeedback;
